/**
 * The campaign list's view rules: which status each campaign is in, how the
 * status tabs filter and count, the search, the order rows appear in, and how
 * dates and ids read in a row or a card.
 */
import {
  campaignStatus,
  formatCampaignTime,
  instantToEasternWallClock,
  TicketSalesCampaignStatus,
  type TicketSalesCampaign,
} from '@bonsai/shared';

export const ALL_CAMPAIGNS = 'all';
export type CampaignListTab = typeof ALL_CAMPAIGNS | TicketSalesCampaignStatus;

/** The order statuses are grouped in on the All tab, and the order of the tabs. */
export const CAMPAIGN_STATUS_ORDER: readonly TicketSalesCampaignStatus[] = [
  TicketSalesCampaignStatus.LIVE,
  TicketSalesCampaignStatus.SCHEDULED,
  TicketSalesCampaignStatus.DISABLED,
  TicketSalesCampaignStatus.ENDED,
];

export const CAMPAIGN_TAB_LABELS: Record<CampaignListTab, string> = {
  [ALL_CAMPAIGNS]: 'All',
  [TicketSalesCampaignStatus.LIVE]: 'Live',
  [TicketSalesCampaignStatus.SCHEDULED]: 'Scheduled',
  [TicketSalesCampaignStatus.DISABLED]: 'Disabled',
  [TicketSalesCampaignStatus.ENDED]: 'Ended',
};

export const CAMPAIGN_TABS: readonly CampaignListTab[] = [ALL_CAMPAIGNS, ...CAMPAIGN_STATUS_ORDER];

export interface CampaignListRow {
  campaign: TicketSalesCampaign;
  status: TicketSalesCampaignStatus;
}

/** Status is worked out against one `now`, so a row's pill and its relative text agree. */
export function toCampaignListRows(
  campaigns: readonly TicketSalesCampaign[],
  now: Date,
): CampaignListRow[] {
  return campaigns.map((campaign) => ({ campaign, status: campaignStatus(campaign, now) }));
}

export function countCampaignsByTab(
  rows: readonly CampaignListRow[],
): Record<CampaignListTab, number> {
  const counts = Object.fromEntries(CAMPAIGN_TABS.map((tab) => [tab, 0])) as Record<
    CampaignListTab,
    number
  >;
  for (const row of rows) counts[row.status] += 1;
  counts[ALL_CAMPAIGNS] = rows.length;
  return counts;
}

const newestCreatedFirst = (a: CampaignListRow, b: CampaignListRow) =>
  b.campaign.createdAt.localeCompare(a.campaign.createdAt);

const byStatusThenNewest = (a: CampaignListRow, b: CampaignListRow) =>
  CAMPAIGN_STATUS_ORDER.indexOf(a.status) - CAMPAIGN_STATUS_ORDER.indexOf(b.status) ||
  newestCreatedFirst(a, b);

/**
 * The rows a tab and search show. All groups by status (live first, so what
 * customers can see right now is on top); within a group, and on a single
 * status tab, the newest-created campaign comes first.
 */
export function visibleCampaignRows(
  rows: readonly CampaignListRow[],
  tab: CampaignListTab,
  search: string,
): CampaignListRow[] {
  const needle = search.trim().toLowerCase();
  return rows
    .filter((row) => tab === ALL_CAMPAIGNS || row.status === tab)
    .filter((row) => row.campaign.name.toLowerCase().includes(needle))
    .sort(tab === ALL_CAMPAIGNS ? byStatusThenNewest : newestCreatedFirst);
}

export function campaignCountLabel(count: number): string {
  return `${count} ${count === 1 ? 'campaign' : 'campaigns'}`;
}

export function campaignEmptyTitle(tab: CampaignListTab, search: string): string {
  const term = search.trim();
  if (term) return `No campaigns match “${term}”`;
  if (tab === ALL_CAMPAIGNS) return 'No campaigns yet';
  return `No ${CAMPAIGN_TAB_LABELS[tab].toLowerCase()} campaigns`;
}

/** The way out of an empty list, or undefined when the viewer has none. */
export function campaignEmptyHint(
  tab: CampaignListTab,
  search: string,
  canCreate: boolean,
): string | undefined {
  if (search.trim()) {
    return tab === ALL_CAMPAIGNS
      ? 'Clear the search or try different words.'
      : 'Clear the search or try another status.';
  }
  if (tab === ALL_CAMPAIGNS) return canCreate ? 'Create one with New campaign.' : undefined;
  return canCreate ? 'Try another status, or create a new campaign.' : 'Try another status.';
}

/** A short handle for telling campaigns apart in the list: the start of the id. */
export function campaignShortId(id: string): string {
  return id.slice(0, 8).toUpperCase();
}

/** "Oct 12, 12:00 AM" in Eastern time — the card's compact form, without weekday or year. */
export function formatCampaignShortDateTime(instant: string): string {
  const [, month, day] = instantToEasternWallClock(instant).split(/[-T]/).map(Number);
  return `${SHORT_MONTHS[month - 1]} ${day}, ${formatCampaignTime(instant)}`;
}

const SHORT_MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];
